import format from "date-fns/format";
import isValid from "date-fns/isValid";
import parse from "date-fns/parse";
import parseISO from "date-fns/parseISO";

const MINUTE_IN_MS = 60 * 1000;
const DATE_TIME_FORMATS = [
  "yyyy-MM-dd HH:mm:ss",
  "yyyy-MM-dd HH:mm",
  "yyyy-MM-dd'T'HH:mm:ss",
  "yyyy-MM-dd'T'HH:mm",
  "yyyy/MM/dd HH:mm:ss",
  "yyyy/MM/dd HH:mm",
];
const OPEN_START_LABEL = "未設定開始時間";
const OPEN_END_LABEL = "未設定結束時間";
const DEFAULT_CONFLICT_WARNING_TYPE = "default_conflict";

function hasDateTimeValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function parseDateTime(value) {
  if (!hasDateTimeValue(value)) {
    return null;
  }

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value === "number") {
    const date = new Date(value);
    return isValid(date) ? date : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim();
  const isoDate = parseISO(normalizedValue);
  if (isValid(isoDate)) {
    return isoDate;
  }

  for (const dateFormat of DATE_TIME_FORMATS) {
    const parsedDate = parse(normalizedValue, dateFormat, new Date(0));
    if (isValid(parsedDate)) {
      return parsedDate;
    }
  }

  return null;
}

function toTimestamp(value) {
  const date = parseDateTime(value);
  return date ? date.getTime() : null;
}

function toTimeRange({ startAt, endAt }) {
  const hasStartAt = hasDateTimeValue(startAt);
  const hasEndAt = hasDateTimeValue(endAt);
  const start = hasStartAt ? toTimestamp(startAt) : Number.NEGATIVE_INFINITY;
  const end = hasEndAt ? toTimestamp(endAt) : Number.POSITIVE_INFINITY;

  if (start === null || end === null || start >= end) {
    return null;
  }

  return { start, end };
}

function formatTimestamp(timestamp) {
  return format(new Date(timestamp), "yyyy/MM/dd HH:mm");
}

function formatPeriodStart(timestamp) {
  return Number.isFinite(timestamp) ? formatTimestamp(timestamp) : OPEN_START_LABEL;
}

function formatPeriodEnd(timestamp) {
  return Number.isFinite(timestamp) ? formatTimestamp(timestamp) : OPEN_END_LABEL;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function overlaps(newRange, menu) {
  const existingRange = toTimeRange({
    startAt: menu.start_at,
    endAt: menu.end_at,
  });

  if (!existingRange) {
    return false;
  }

  return newRange.start < existingRange.end && existingRange.start < newRange.end;
}

export function findOverlappingDefaultMenus({
  menus,
  currentMenuId,
  startAt,
  endAt,
}) {
  const newRange = toTimeRange({ startAt, endAt });

  if (!newRange) {
    return [];
  }

  return (menus || []).filter((menu) => {
    if (currentMenuId && String(menu.id) === String(currentMenuId)) {
      return false;
    }

    if (!menu.is_default) {
      return false;
    }

    return overlaps(newRange, menu);
  });
}

export function getDefaultMenuGapPeriods({ affectedMenus, startAt, endAt }) {
  const newRange = toTimeRange({ startAt, endAt });

  if (!newRange) {
    return [];
  }

  return (affectedMenus || []).flatMap((menu) => {
    const existingRange = toTimeRange({
      startAt: menu.start_at,
      endAt: menu.end_at,
    });
    const periods = [];

    if (!existingRange) {
      return periods;
    }

    if (existingRange.start < newRange.start) {
      const gapEnd = Math.min(existingRange.end, newRange.start) - MINUTE_IN_MS;
      if (existingRange.start <= gapEnd) {
        periods.push({
          start_at: formatPeriodStart(existingRange.start),
          end_at: formatPeriodEnd(gapEnd),
        });
      }
    }

    if (existingRange.end > newRange.end) {
      const gapStart = Math.max(existingRange.start, newRange.end) + MINUTE_IN_MS;
      if (gapStart <= existingRange.end) {
        periods.push({
          start_at: formatPeriodStart(gapStart),
          end_at: formatPeriodEnd(existingRange.end),
        });
      }
    }

    return periods;
  });
}

export function filterConfirmedDefaultConflictWarnings({
  warnings,
  defaultConflictConfirmed,
}) {
  if (!defaultConflictConfirmed || !Array.isArray(warnings)) {
    return warnings;
  }

  return warnings.filter((warning) => {
    return warning.type !== DEFAULT_CONFLICT_WARNING_TYPE;
  });
}

export function buildDefaultConflictHtml({ affectedMenus, gapPeriods }) {
  const affectedTitles = (affectedMenus || [])
    .map((menu) => escapeHtml(menu.title))
    .join("、");
  const gapHtml = (gapPeriods || [])
    .map((period) => `<div>${period.start_at} ~ ${period.end_at}</div>`)
    .join("");

  return `
    <div style="text-align: left;">
      <p>此時段已有預設圖文選單：${affectedTitles}</p>
      <p>是否將此檔設定為『預設圖文選單』並覆蓋原設定？</p>
      ${
        gapHtml
          ? `
            <hr style="border: 0; border-top: 1px solid #e5e5e5; margin: 16px 0;" />
            <p style="color: #d9534f; margin-top: 16px;">
              ⚠️提醒：異動後，下列時段將 “無”設為預設的選單：
            </p>
            ${gapHtml}
          `
          : ""
      }
    </div>
  `;
}
